import React from "react";
import { IMPACT_METRICS } from "../data/content";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Evidence strip — compact single-row at wide widths, 3×2 at mid, 2×3 on mobile.
 * Switches to 3-col BEFORE collisions can occur. No text truncation on labels.
 */
export default function ImpactStrip() {
  const prefersReduced = useReducedMotion();
  const metrics = IMPACT_METRICS.slice(0, 6);
  return (
    <section
      className="relative bg-[#0B0B0C] border-b border-[#343437] grain"
      data-testid="home-impact-strip"
      aria-labelledby="home-impact-strip-title"
    >
      {/* Gold metallic-dust — radial washes concentrated at edges */}
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            "radial-gradient(1200px 300px at 50% 100%, rgba(216,169,54,0.10), transparent 65%), radial-gradient(600px 200px at 8% 0%, rgba(216,169,54,0.05), transparent 60%)",
        }}
        aria-hidden
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.055] mix-blend-screen"
        style={{
          backgroundImage:
            "radial-gradient(circle at 12% 40%, #D8A936 0.6px, transparent 1.2px), radial-gradient(circle at 68% 15%, #D8A936 0.5px, transparent 1.2px), radial-gradient(circle at 90% 85%, #D8A936 0.6px, transparent 1.2px), radial-gradient(circle at 32% 90%, #D8A936 0.4px, transparent 1px)",
          backgroundSize: "180px 180px, 220px 220px, 260px 260px, 140px 140px",
        }}
        aria-hidden
      />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-12">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-7 lg:mb-8">
          <p id="home-impact-strip-title" className="eyebrow">Evidence · Verified</p>
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6] font-display">
            Real programs · Real scale
          </p>
        </div>
        {/*
          Column layout:
            mobile  (default)       → 2 cols
            md 768 – xl 1280        → 3 cols × 2 rows   (prevents collision below 1280)
            xl 1280+                → 6 cols × 1 row    (approved mock)
        */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-5 gap-y-9">
          {metrics.map((m, i) => (
            <motion.div
              key={m.id}
              initial={prefersReduced ? false : { opacity: 0, y: 12 }}
              whileInView={prefersReduced ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className={`relative px-3 xl:px-4 min-w-0 ${i > 0 ? "xl:border-l xl:border-[#343437]" : ""} ${i % 3 !== 0 ? "md:xl:border-0 md:border-l md:border-[#343437]" : ""}`}
              data-testid={`impact-strip-${m.id}`}
            >
              <p className="oversize text-[28px] md:text-[32px] lg:text-[36px] xl:text-[38px] leading-none text-[#D8A936] tabular-nums">
                {m.value}
              </p>
              <p className="mt-3 font-display text-[12.5px] lg:text-[13px] text-[#FAF9F6] leading-snug">
                {m.label}
              </p>
              <p className="mt-1.5 text-[10px] tracking-[0.12em] uppercase text-[#A9A9A6] leading-tight">
                {m.context.split(" — ")[0]}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
